// Ported from genieos/web/src/components/gamma/Button.tsx @ 84923d036
import { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/utils/cn';
import type { ButtonProps, ButtonVariant, ButtonSize } from './types';

const variantStyles: Record<ButtonVariant, string> = {
  default: 'bg-ems-bg-card-hover text-ems-text-primary border border-ems-border hover:border-ems-border-bright',
  primary: 'bg-ems-forest text-ems-green border border-ems-green/20 hover:bg-ems-green/20',
  secondary: 'bg-ems-bg-card-hover text-ems-text-secondary border border-ems-border hover:text-ems-text-primary hover:border-ems-border-bright',
  ghost: 'bg-transparent text-ems-text-secondary border border-transparent hover:bg-ems-bg-card-hover hover:text-ems-text-primary',
  outline: 'bg-transparent text-ems-text-primary border border-ems-border hover:bg-ems-bg-card hover:border-ems-border-bright',
  destructive: 'bg-ems-red-deep/20 text-ems-red border border-ems-red/20 hover:bg-ems-red-deep/30',
  danger: 'bg-ems-red-deep/20 text-ems-red border border-ems-red/20 hover:bg-ems-red-deep/30',
  emergency: 'bg-ems-red-deep text-white font-bold uppercase border-2 border-red-400/40 animate-pulse',
  success: 'bg-ems-forest/40 text-ems-green border border-ems-green/20 hover:bg-ems-green/20',
  warning: 'bg-amber-900/40 text-ems-amber border border-ems-amber/20 hover:bg-amber-900/60',
  info: 'bg-ems-blue/10 text-ems-blue border border-ems-blue/20 hover:bg-ems-blue/20',
  error: 'bg-ems-red-deep/20 text-ems-red border border-ems-red/20 hover:bg-ems-red-deep/30',
  purple: 'bg-purple-500/20 text-purple-400 border border-purple-500/20 hover:bg-purple-500/30',
};

const sizeStyles: Record<ButtonSize, string> = {
  xs: 'px-2 py-1 text-xs min-h-[28px]',
  sm: 'px-3 py-1.5 text-sm min-h-[36px]',
  md: 'px-4 py-2 text-sm min-h-[44px]',
  lg: 'px-6 py-3 text-base min-h-[52px]',
  xl: 'px-8 py-4 text-lg min-h-[60px]',
};

const LoadingSpinner = () => (
  <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
);

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(({
  variant = 'secondary',
  size = 'md',
  loading = false,
  loadingText,
  leftIcon,
  rightIcon,
  emergency = false,
  className,
  children,
  disabled,
  // Legacy props — accepted but ignored
  industrial: _industrial,
  touchOptimized: _touchOptimized,
  critical: _critical,
  // Exclude HTML events that conflict with framer-motion
  onAnimationStart: _onAnimationStart,
  onAnimationEnd: _onAnimationEnd,
  onAnimationIteration: _onAnimationIteration,
  onDragStart: _onDragStart,
  onDragEnd: _onDragEnd,
  onDrag: _onDrag,
  ...props
}, ref) => {
  const isDisabled = disabled || loading;
  const effectiveVariant = emergency ? 'emergency' : variant;

  return (
    <motion.button
      ref={ref}
      className={cn(
        'inline-flex items-center justify-center gap-2 rounded-lg font-medium',
        'border transition-colors touch-manipulation',
        'focus:outline-none focus:ring-2 focus:ring-ems-green/30',
        'disabled:opacity-40 disabled:cursor-not-allowed',
        variantStyles[effectiveVariant] || variantStyles.secondary,
        sizeStyles[size],
        className
      )}
      whileHover={!isDisabled ? { scale: 1.02 } : undefined}
      whileTap={!isDisabled ? { scale: 0.98 } : undefined}
      disabled={isDisabled}
      {...props}
    >
      {loading && <LoadingSpinner />}
      {!loading && leftIcon && (
        <span className="flex-shrink-0" aria-hidden="true">
          {leftIcon}
        </span>
      )}
      <span className="font-medium">
        {loading ? loadingText || 'Loading...' : children}
      </span>
      {!loading && rightIcon && (
        <span className="flex-shrink-0" aria-hidden="true">
          {rightIcon}
        </span>
      )}
    </motion.button>
  );
});

Button.displayName = 'Button';
